import {
  BookOpen,
  Building2,
  CalendarDays,
  CircleHelp,
  ClipboardList,
  FolderOpen,
  HeartHandshake,
  Home,
  LayoutDashboard,
  ScrollText,
  Settings,
  Shield,
  Users,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { NavMain } from "@/components/nav-main";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { homePath } from "@/auth/homePath";
import { useAuth } from "@/auth/useAuth";
import { usePermissions } from "@/hooks/usePermissions";

const TONE = {
  indigo: "text-indigo-600 dark:text-indigo-400",
  cyan: "text-cyan-600 dark:text-cyan-400",
  emerald: "text-emerald-600 dark:text-emerald-400",
  amber: "text-amber-600 dark:text-amber-400",
  rose: "text-rose-600 dark:text-rose-400",
  slate: "text-slate-600 dark:text-slate-400",
};

export function AppSidebar({
  onSignOut,
  signingOut,
  ...props
}) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const { pathname } = useLocation();
  const isPlatform = Boolean(user?.is_superuser);
  const dashboardUrl = homePath(user);

  const brandName = isPlatform
    ? "Homecare SaaS"
    : user?.agency?.name || "Homecare Agency";

  const careItems = isPlatform
    ? []
    : [
        can("scheduling.visits.view") && {
          title: "Schedule",
          url: "/schedule",
          icon: CalendarDays,
        },
        can("careplans.plans.view") && {
          title: "Care Plans",
          url: "/care-plans",
          icon: ClipboardList,
        },
        can("directory.clients.view") && {
          title: "Clients",
          url: "/clients",
          icon: Users,
        },
        can("directory.caregivers.view") && {
          title: "Caregivers",
          url: "/caregivers",
          icon: HeartHandshake,
        },
      ].filter(Boolean);

  const adminItems = isPlatform
    ? [
        { title: "Organizations", url: "/admin/agencies", icon: Building2 },
        { title: "Users", url: "/admin/users", icon: Users },
        { title: "Roles & Permissions", url: "/admin/roles", icon: Shield },
      ]
    : [
        can("organization.users.view") && {
          title: "Team Members",
          url: "/settings/users",
          icon: Users,
        },
        can("organization.settings.view") && {
          title: "Organization",
          url: "/settings/org",
          icon: Building2,
        },
      ].filter(Boolean);

  const mainNav = [
    {
      title: "Dashboard",
      url: dashboardUrl,
      icon: LayoutDashboard,
      iconClassName: TONE.indigo,
      end: true,
    },
    careItems.length > 0 && {
      title: "Care",
      icon: FolderOpen,
      iconClassName: TONE.cyan,
      isActive:
        pathname.startsWith("/schedule") ||
        pathname.startsWith("/care-plans") ||
        pathname.startsWith("/clients") ||
        pathname.startsWith("/caregivers") ||
        pathname.startsWith("/visits"),
      items: careItems,
    },
    adminItems.length > 0 && {
      title: isPlatform ? "Platform" : "Organization",
      icon: isPlatform ? Building2 : Settings,
      iconClassName: TONE.amber,
      isActive:
        pathname.startsWith("/admin") || pathname.startsWith("/settings"),
      items: adminItems,
    },
    can("audit.logs.view") && {
      title: "Compliance",
      icon: ScrollText,
      iconClassName: TONE.slate,
      isActive: pathname.startsWith("/audit-logs"),
      items: [{ title: "Audit Logs", url: "/audit-logs", icon: ScrollText }],
    },
  ].filter(Boolean);

  const quickCreateTo = isPlatform
    ? "/admin/agencies/new"
    : can("scheduling.visits.create") || can("scheduling.visits.edit")
      ? "/schedule"
      : "/clients/new";

  const quickCreateLabel = isPlatform ? "Register Org" : "Quick Create";

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:!p-1.5">
              <Link to={dashboardUrl}>
                <Home className="!size-5 text-indigo-600 dark:text-indigo-400" />
                <span className="text-base font-semibold">{brandName}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain
          label={isPlatform ? "Platform" : "Organization"}
          items={mainNav}
          quickCreateTo={quickCreateTo}
          quickCreateLabel={quickCreateLabel}
        />
        <NavSecondary
          items={[
            { title: "Get Help", url: "/help", icon: CircleHelp, iconClassName: TONE.rose },
            { title: "Docs", url: "/docs", icon: BookOpen, iconClassName: TONE.indigo },
          ]}
          className="mt-auto"
        />
      </SidebarContent>
      <SidebarFooter>
        <NavUser
          user={{
            name: user?.full_name || user?.email || "Account",
            email: user?.email || "",
            role: isPlatform ? "Platform Superuser" : user?.role || "Staff",
          }}
          onSignOut={onSignOut}
          signingOut={signingOut}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
